import { Injectable, inject } from '@angular/core';
import { ApiService, GenericResponseCode } from '@brighthub/api';
import { Observable, map } from 'rxjs';
import {
  CaseApiEnvelope,
  CaseListPayload,
  CaseStatusesPayload,
  CaseClosedReasonsPayload,
  CaseResource,
  CaseStatusTransitionResource,
  CreateCaseRequest,
  DomainListPayload,
  DomainResource,
  ObjectiveTypeListPayload,
  ObjectiveTypeResource,
  QueryCasesRequest,
  TransitionCaseStatusRequest,
  UpdateCaseRequest,
} from './case-api.model';
import { CaseApi } from './case-api.token';
import { SystemCodeCatalogService } from '../../system-codes/system-code-catalog.service';

const API_NAME = 'api-internal';
const CASE_PATH = '/case';

/** Live authenticated client for AIM's core Case endpoints. */
@Injectable()
export class HttpCaseApiService implements CaseApi {
  private readonly _api = inject(ApiService);
  private readonly _systemCodes = inject(SystemCodeCatalogService);

  listCaseStatuses(): Observable<CaseStatusesPayload> {
    return this._systemCodes.load().pipe(
      map((catalog) => ({
        CASE_STATUS: this._systemCodes
          .values(catalog, 'case_status')
          .map(({ code, label, systemCodeValueId }) => ({
            code,
            value: label,
            systemCodeValueId,
          })),
      })),
    );
  }

  listCaseClosedReasons(): Observable<CaseClosedReasonsPayload> {
    return this._systemCodes.load().pipe(
      map((catalog) => ({
        CASE_CLOSED_REASON: this._systemCodes
          .values(catalog, 'case_closed_reason')
          .map(({ code, label, systemCodeValueId }) => ({
            code,
            value: label,
            systemCodeValueId,
          })),
      })),
    );
  }

  createCase(body: CreateCaseRequest): Observable<CaseResource> {
    return this._api
      .post<CaseApiEnvelope<CaseResource>>(API_NAME, CASE_PATH, body, {
        suppressRequestBodyTelemetry: true,
        telemetryMetadata: {
          clientId: body.clientId,
          domainId: body.domainId,
          objectiveTypeId: body.objectiveTypeId,
          statusCode: body.statusCode,
        },
      })
      .pipe(map((response) => this._payload(response)));
  }

  updateCase(
    caseId: number,
    body: UpdateCaseRequest,
  ): Observable<CaseResource> {
    return this._api
      .patch<CaseApiEnvelope<CaseResource>>(
        API_NAME,
        this._casePath(caseId),
        body,
        {
          suppressRequestBodyTelemetry: true,
          telemetryMetadata: { caseId },
        },
      )
      .pipe(map((response) => this._payload(response)));
  }

  getCase(caseId: number): Observable<CaseResource> {
    return this._api
      .get<CaseApiEnvelope<CaseResource>>(API_NAME, this._casePath(caseId))
      .pipe(map((response) => this._payload(response)));
  }

  queryCases(body: QueryCasesRequest): Observable<CaseListPayload> {
    return this._api
      .get<CaseApiEnvelope<CaseListPayload>>(API_NAME, this._queryPath(body))
      .pipe(map((response) => this._payload(response)));
  }

  listDomains(): Observable<DomainResource[]> {
    return this._api
      .get<CaseApiEnvelope<DomainListPayload>>(API_NAME, '/domain')
      .pipe(map((response) => this._payload(response).domains));
  }

  listObjectiveTypes(domainId: number): Observable<ObjectiveTypeResource[]> {
    const params = new URLSearchParams({ domainId: String(domainId) });
    return this._api
      .get<
        CaseApiEnvelope<ObjectiveTypeListPayload>
      >(API_NAME, `/objective-type?${params.toString()}`)
      .pipe(map((response) => this._payload(response).objectiveTypes));
  }

  transitionCaseStatus(
    caseId: number,
    body: TransitionCaseStatusRequest,
  ): Observable<CaseStatusTransitionResource> {
    return this._api
      .post<CaseApiEnvelope<CaseStatusTransitionResource>>(
        API_NAME,
        `${this._casePath(caseId)}/status-transitions`,
        body,
        {
          suppressRequestBodyTelemetry: true,
          telemetryMetadata: {
            caseId,
            toStatusCode: body.toStatusCode,
            closedReasonCode: body.closedReasonCode,
            expectedCurrentStatusCode: body.expectedCurrentStatusCode,
          },
        },
      )
      .pipe(map((response) => this._payload(response)));
  }

  private _casePath(caseId: number): string {
    return `${CASE_PATH}/${encodeURIComponent(String(caseId))}`;
  }

  private _queryPath(filters: QueryCasesRequest): string {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) params.set(key, String(value));
    });
    const query = params.toString();
    return query ? `${CASE_PATH}?${query}` : CASE_PATH;
  }

  private _payload<T>(response: CaseApiEnvelope<T>): T {
    if (
      response?.result?.code !== GenericResponseCode.SERVICE_OK ||
      response.payload == null
    ) {
      throw new Error(
        response?.result?.message || 'Case API returned an invalid response',
      );
    }
    return response.payload;
  }
}
